import React, { useState, useCallback, useEffect, useRef } from 'react';
import QuizContent from '../shared/QuizContent';
import QuizTitle from '../shared/QuizTitle';
import QuizOptions from '../shared/QuizOptions';
import QuizOption from '../shared/QuizOption';
import LazyImage from '../shared/LazyImage';
import { getQuizOptionsData } from '../../constants';
import { withFirstFramePoster } from '../../utils/video';
import { loadCharacterAttributes, getAttributeId as getAttrId } from '../../utils/api';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { trackStep3 } from '../../utils/analytics';
import { useDesign } from '../../contexts/DesignContext';
import { useVideoAnimation } from '../../hooks/useVideoAnimation';
import iconRose from '../../assets/valentinesDay/iconRose.png';
import heartWhite from '../../assets/valentinesDay/heartWhite.png';

const IGNORE_VIDEO_ERRORS = ['Toned_ass_Anime'];

const Step2 = ({ onAnswer, onPrevious, selections, isTransitioning }) => {
  const { isNewDesign } = useDesign();
  const {
    animationMode,
    isMobile,
    shouldPlayOnHoverOrSelected,
    shouldShowVideos,
    getVideoProps,
  } = useVideoAnimation();
  const location = useLocation();
  const selectedStyle = location.pathname.includes('/create-ai-hentai')
    ? 'Anime'
    : location.pathname.includes('/create-ai-porn')
    ? 'Realistic'
    : selections?.welcome || 'Realistic';

  const bodyTypes = getQuizOptionsData('bodyType', selectedStyle);
  const breastSizes = getQuizOptionsData('breastSize', selectedStyle);
  const bootySizes = getQuizOptionsData('buttSize', selectedStyle);

  const [selectedBodyType, setSelectedBodyType] = useState(
    bodyTypes[0]?.value || '',
  );
  const [selectedBreastSize, setSelectedBreastSize] = useState(
    breastSizes[0]?.value || '',
  );
  const [selectedBootySize, setSelectedBootySize] = useState(
    bootySizes[0]?.value || '',
  );
  const [videoErrors, setVideoErrors] = useState({});
  const videoRefs = useRef({});

  const isHalloween = !!location.pathname.includes('/halloween');

  const shouldShowVideo =
    shouldShowVideos &&
    (selectedStyle === 'Realistic' || selectedStyle === 'Anime');

  useEffect(() => {
    setVideoErrors({});
  }, [selectedStyle]);

  useEffect(() => {
    loadCharacterAttributes();
  }, []);

  useEffect(() => {
    if (!shouldPlayOnHoverOrSelected) return;

    Object.keys(videoRefs.current).forEach((key) => {
      const video = videoRefs.current[key];
      if (video && !video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    });

    const playSelectedVideo = (videoKey) => {
      const video = videoRefs.current[videoKey];
      if (video && video.paused) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    };

    if (selectedBodyType) playSelectedVideo(selectedBodyType);
    if (selectedBreastSize) playSelectedVideo(selectedBreastSize);
    if (selectedBootySize) playSelectedVideo(selectedBootySize);
  }, [
    selectedBodyType,
    selectedBreastSize,
    selectedBootySize,
    shouldPlayOnHoverOrSelected,
  ]);

  const getAttributeId = (type, label) => getAttrId(type, label, selectedStyle, 'Girls');

  const handleBodyTypeClick = useCallback(
    (value) => {
      if (isTransitioning) return;
      setSelectedBodyType(value);
    },
    [isTransitioning],
  );

  const handleBreastSizeClick = useCallback(
    (value) => {
      if (isTransitioning ) return;
      setSelectedBreastSize(value);
    },
    [isTransitioning,],
  );

  const handleBootySizeClick = useCallback(
    (value) => {
      if (isTransitioning ) return;
      setSelectedBootySize(value);
    },
    [isTransitioning],
  );

  const handleContinue = useCallback(() => {
    if (isTransitioning || !canContinue) return;

    trackStep3(selectedBodyType, selectedBreastSize, selectedBootySize);

    onAnswer('step2', {
      bodyType: selectedBodyType,
      breastSize: selectedBreastSize,
      buttSize: selectedBootySize,
    });
  }, [
    selectedBodyType,
    selectedBreastSize,
    selectedBootySize,
    onAnswer,
    isTransitioning,
  ]);

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return;
    onPrevious();
  }, [onPrevious, isTransitioning]);

  const canContinue =
    selectedBodyType && selectedBreastSize && selectedBootySize;
  const { t } = useTranslation();

  const handleVideoError = useCallback((itemValue) => {
    if (IGNORE_VIDEO_ERRORS.includes(itemValue)) {
      return;
    }
    setVideoErrors((prev) => ({ ...prev, [itemValue]: true }));
  }, []);

  const handleVideoMouseEnter = useCallback(
    (e, isSelected) => {
      if (shouldPlayOnHoverOrSelected && !isMobile && e.target.paused) {
        e.target.currentTime = 0;
        e.target.play().catch(() => {});
      }
    },
    [shouldPlayOnHoverOrSelected, isMobile],
  );

  const handleVideoMouseLeave = useCallback(
    (e, isSelected) => {
      if (
        shouldPlayOnHoverOrSelected &&
        !isMobile &&
        !e.target.paused &&
        !isSelected
      ) {
        e.target.pause();
        e.target.currentTime = 0;
      }
    },
    [shouldPlayOnHoverOrSelected, isMobile],
  );

  const handleVideoClick = useCallback(
    (e) => {
      if (animationMode === 'MOUSEOVER' && isMobile) {
        if (e.target.paused) {
          e.target.currentTime = 0;
          e.target.play().catch(() => {});
        } else {
          e.target.pause();
          e.target.currentTime = 0;
        }
      }
    },
    [animationMode, isMobile],
  );

  const renderMedia = (item, isSelected) => {
    if (!shouldShowVideo || !item.video || videoErrors[item.value]) {
      return <LazyImage src={item.image} alt={item.alt} />;
    }

    return (
      <video
        ref={(el) => (videoRefs.current[item.value] = el)}
        key={item.value}
        src={withFirstFramePoster(item.video)}
        {...getVideoProps()}
        preload="metadata"
        onMouseEnter={(e) => handleVideoMouseEnter(e, isSelected)}
        onMouseLeave={(e) => handleVideoMouseLeave(e, isSelected)}
        onClick={handleVideoClick}
        onError={() => handleVideoError(item.value)}
      />
    );
  };

  return (
    <div className={`quizContentWrapper ${isTransitioning ? 'no-click' : ''}`}>
      <img
        src={iconRose}
        alt=""
        className="step2-icon-rose"
        aria-hidden="true"
      />
      <QuizContent className="step2-design">
        {/* Body Type Block */}
        <div className="step2-block">
          <QuizTitle>
            {isHalloween || isNewDesign ? (
              <>
                {t('choose')}
                <span className="specialText"> {t('body_text')}</span>
              </>
            ) : (
              t('choose_body')
            )}
          </QuizTitle>
          <QuizOptions className="body-options">
            {bodyTypes.map((type) => {
              const isSelected = selectedBodyType === type.value;
              return (
                <div key={type.value} className="option-wrapper">
                  <QuizOption
                    value={type.value}
                    name="bodyType"
                    onClick={() => handleBodyTypeClick(type.value)}
                    className={`body-option ${
                      isSelected
                        ? 'selected'
                        : selectedBodyType
                        ? 'not-selected'
                        : ''
                    } ${bodyTypes.length % 2 === 0 ? 'even' : 'odd'}`}
                  >
                    <div className="body-image">
                      {renderMedia(type, isSelected)}
                    </div>
                    <div className="image-title">{t(type.text)}</div>
                  </QuizOption>
                </div>
              );
            })}
          </QuizOptions>
        </div>

        {/* Breast Size Block */}
        <div className="step2-block">
          <QuizTitle>
            {isHalloween || isNewDesign ? (
              <>
                {t('choose')}
                <span className="specialText"> {t('breast_text')}</span>
              </>
            ) : (
              t('choose_breast')
            )}
            <img
              src={heartWhite}
              alt=""
              className="valentine-title-icon"
              aria-hidden="true"
            />
          </QuizTitle>
          <QuizOptions className="breast-options">
            {breastSizes.map((size) => {
              const isSelected = selectedBreastSize === size.value;
              return (
                <div key={size.value} className="option-wrapper">
                  <QuizOption
                    value={size.value}
                    name="breastSize"
                    onClick={() => handleBreastSizeClick(size.value)}
                    className={`breast-option ${
                      isSelected
                        ? 'selected'
                        : selectedBreastSize
                        ? 'not-selected'
                        : ''
                    } ${breastSizes.length % 2 === 0 ? 'even' : 'odd'}`}
                  >
                    <div className="breast-image">
                      {renderMedia(size, isSelected)}
                    </div>
                    <div className="image-title">{t(size.text)}</div>
                  </QuizOption>
                </div>
              );
            })}
          </QuizOptions>
        </div>

        {/* Booty Size Block */}
        <div className="step2-block">
          <QuizTitle>
            {isHalloween || isNewDesign ? (
              <>
                {t('choose')}
                <span className="specialText"> {t('booty_text')}</span>
              </>
            ) : (
              t('choose_booty')
            )}
          </QuizTitle>
          <QuizOptions className="booty-options">
            {bootySizes.map((size) => {
              const isSelected = selectedBootySize === size.value;
              return (
                <div key={size.value} className="option-wrapper">
                  <QuizOption
                    value={size.value}
                    name="bootySize"
                    onClick={() => handleBootySizeClick(size.value)}
                    className={`booty-option ${
                      isSelected
                        ? 'selected'
                        : selectedBootySize
                        ? 'not-selected'
                        : ''
                    } ${bootySizes.length % 2 === 0 ? 'even' : 'odd'}`}
                  >
                    <div className="booty-image">
                      {renderMedia(size, isSelected)}
                    </div>
                    <div className="image-title">{t(size.text)}</div>
                  </QuizOption>
                </div>
              );
            })}
          </QuizOptions>
        </div>

        <div className="navigation-wrapper step2-navigation-wrapper">
          <button
            className="previous-button"
            onClick={handlePrevious}
            disabled={isTransitioning}
          >
            {t('previous')}
          </button>
          <button
            className="continue-button"
            onClick={handleContinue}
            disabled={!canContinue || isTransitioning }
          >
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  );
};

export default Step2;
